import { useTranslation } from 'react-i18next'

import { formatNoteTimestamp, formatUsd } from '#/shared/lib/format'
import { cn } from '#/shared/lib/utils'

import { deriveStatusBanner } from '../model/status-banner'
import type { StatusBannerVariant } from '../model/status-banner'
import type { CarrierRequestItem } from '../model/types'

const VARIANT_STYLES: Record<StatusBannerVariant, string> = {
  carrierPlacedBid: 'bg-blue-50 text-blue-500',
  offerSent: 'bg-green-50 text-green-500',
  counterOfferSent: 'bg-green-50 text-green-500',
  carrierAccepted: 'bg-green-50 text-green-500',
  autoDeclined: 'bg-gray-100 text-gray-500',
  carrierCanceled: 'bg-red-100 text-red-500',
  offerCanceled: 'bg-gray-100 text-gray-500',
}

const VARIANT_LABEL_KEYS: Record<StatusBannerVariant, string> = {
  carrierPlacedBid: 'carrierRequests.carrierPlacedBid',
  offerSent: 'carrierRequests.offerSentFor',
  counterOfferSent: 'carrierRequests.counterOfferSentFor',
  carrierAccepted: 'carrierRequests.carrierAcceptedOfferFor',
  autoDeclined: 'carrierRequests.requestAutoDeclined',
  carrierCanceled: 'carrierRequests.carrierCanceledRequest',
  offerCanceled: 'carrierRequests.offerCanceled',
}

/**
 * Amount-less wording for the variants whose default label interpolates one —
 * without it a request that carries no price would render a raw `{{amount}}`.
 */
const AMOUNTLESS_LABEL_KEYS: Partial<Record<StatusBannerVariant, string>> = {
  offerSent: 'carrierRequests.offerSent',
  counterOfferSent: 'carrierRequests.counterOfferSent',
  carrierAccepted: 'carrierRequests.carrierAcceptedOffer',
}

export function RequestStatusBanner({
  request,
}: {
  request: CarrierRequestItem
}) {
  const { t } = useTranslation()
  const banner = deriveStatusBanner(request)
  if (!banner) return null
  const label =
    banner.amount != null
      ? t(VARIANT_LABEL_KEYS[banner.variant], {
          amount: formatUsd(banner.amount),
        })
      : t(
          AMOUNTLESS_LABEL_KEYS[banner.variant] ??
            VARIANT_LABEL_KEYS[banner.variant],
        )
  return (
    <div className="flex items-center gap-2.5 text-xs">
      <span
        className={cn(
          'inline-flex items-center rounded-md px-1.75 py-0.75',
          VARIANT_STYLES[banner.variant],
        )}
      >
        {label}
      </span>
      {banner.at && (
        <span className="text-gray-500">{formatNoteTimestamp(banner.at)}</span>
      )}
    </div>
  )
}
